import { isCoordinate, rectangle } from "@/math/coordinates";
import type { Rectangle, RelativeCoordinate } from "@/model/coordinates";
import { relativeCoord } from "@/model/coordinates";
import { fixLine } from "@/model/shape";
import { useGlobalI18n } from "@/translations/i18n";

import { BoardLocation, borderLines } from "./BoardLocation";

const { t } = useGlobalI18n();

const names = [
  /*$t*/ "risk.resolved",
  /*$t*/ "risk.owned",
  /*$t*/ "risk.accepted",
  /*$t*/ "risk.mitigated",
  /*$t*/ "riskBoardLocation.submissions",
];
const roam = ["R", "O", "A", "M", ""];

export class RiskBoardLocation extends BoardLocation {
  constructor(private regionIndex: number) {
    super();
  }

  static of(value: RelativeCoordinate | number | string): RiskBoardLocation {
    return isCoordinate(value)
      ? RiskBoardLocation.fromCoordinate(value)
      : new RiskBoardLocation(+value);
  }

  static fromCoordinate(coord: RelativeCoordinate): RiskBoardLocation {
    if (coord.y < 0.5) {
      return new RiskBoardLocation(Math.floor(coord.x * 2));
    }
    if (coord.y < 0.85) {
      return new RiskBoardLocation(2 + Math.floor(coord.x * 2));
    }
    return new RiskBoardLocation(4);
  }

  override get shapes() {
    return [
      ...borderLines,
      fixLine("v", relativeCoord(0.5, 0), relativeCoord(0.5, 0.85)),
      fixLine("h1", relativeCoord(0, 0.5), relativeCoord(1, 0.5)),
      fixLine("h2", relativeCoord(0, 0.85), relativeCoord(1, 0.85)),
    ];
  }

  override get bounds(): Rectangle<RelativeCoordinate> {
    return rectangle({
      from: this.topLeft,
      size: this.regionIndex < 4 ? relativeCoord(0.5, 0.34) : relativeCoord(1, 0.18),
    });
  }

  override get freeBounds(): Rectangle<RelativeCoordinate> {
    if (this.regionIndex < 2) {
      return rectangle({
        from: relativeCoord(0.5 * (this.regionIndex % 2), 0.12),
        size: relativeCoord(0.5, 0.39),
      });
    }
    if (this.regionIndex < 4) {
      return rectangle({
        from: relativeCoord(0.5 * (this.regionIndex % 2), 0.6),
        size: relativeCoord(0.5, 0.26),
      });
    }
    return rectangle({ from: relativeCoord(0, 0.9), size: relativeCoord(1, 0.1) });
  }

  private get topLeft(): RelativeCoordinate {
    if (this.regionIndex < 2) {
      return relativeCoord(0.5 * (this.regionIndex % 2), 0.17);
    }
    if (this.regionIndex < 4) {
      return relativeCoord(0.5 * (this.regionIndex % 2), 0.52);
    }
    return relativeCoord(0, 0.82);
  }

  override matches(coord: RelativeCoordinate) {
    return RiskBoardLocation.fromCoordinate(coord).regionIndex === this.regionIndex;
  }

  get roam() {
    return roam[this.regionIndex];
  }

  override id() {
    return "" + this.regionIndex;
  }

  override get name(): string {
    return t(names[this.regionIndex]);
  }
}
